import type React from 'react';

import { Link, Text } from '@change/ds-core-ui';
import { SparkleIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { isSsr } from '@change-corgi/core/ssr';
import { getWindow } from '@change-corgi/core/window';
import { Translate } from '@change-corgi/core-react/i18n';

import { ReportAIIssueLink } from '#src/app/shared/components/abuse';

import { useProfileCard } from '../../hooks/useProfileCard';

export const Description: React.FC = () => {
	const {
		data: { descriptionText, descriptionClamped, isDescriptionClampable, displayAbuseLink, isAiGeneratedDescription },
		actions: { handleShowFullTextClick },
	} = useProfileCard();

	return (
		<>
			<Text
				as="p"
				data-qa="dm-description"
				variant="body"
				className={cn(
					{
						'lg:line-clamp-3': isDescriptionClampable.desktop && descriptionClamped,
						'line-clamp-3': isDescriptionClampable.mobile && descriptionClamped,
					},
					'text-typography-strong mt-8',
				)}
			>
				{descriptionText}
			</Text>

			<Link
				onClick={handleShowFullTextClick}
				variant="secondary"
				data-qa="dm-description-toggle"
				className={cn('mt-1 hidden', {
					'sm:inline-block': isDescriptionClampable.mobile,
					'md:hidden': isDescriptionClampable.mobile && !isDescriptionClampable.desktop,
					'lg:inline-block': isDescriptionClampable.desktop,
				})}
			>
				{descriptionClamped ? (
					<Translate value="corgi.decision_maker_profile.profile_section.description.show_full_text" />
				) : (
					<Translate value="corgi.decision_maker_profile.profile_section.description.show_less" />
				)}
			</Link>

			{descriptionText && (displayAbuseLink || isAiGeneratedDescription) && (
				<div className="mt-4 flex items-start gap-6" data-qa="dm-description-report-ai-issue">
					{displayAbuseLink && (
						// This is not a component built with the Tomate-DS.
						<ReportAIIssueLink pageUrl={isSsr() ? undefined : getWindow().location.href} type="generic" />
					)}

					{isAiGeneratedDescription && (
						<div className="flex items-center" data-qa="dm-description-ai-summary-badge">
							<SparkleIcon size={16} className="text-icon-weak mr-1" />
							<Text as="span" size="x-small" className="text-typography-weak">
								<Translate value="corgi.decision_maker_profile.profile_section.description.ai_summary_badge_label" />
							</Text>
						</div>
					)}
				</div>
			)}
		</>
	);
};
